import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtUSD } from "../lib/api";
import Dashboard from "./Dashboard";
import EconWidget from "../components/econ/EconWidget";
import { Sparkles, ArrowUpRight, ArrowDownRight } from "lucide-react";

function KpiCell({ k }) {
  const val = () => {
    if (k.fmt === "usd") return fmtUSD(k.value, { compact: true });
    if (k.fmt === "usd_delta") {
      const up = k.value >= 0;
      return (
        <span className={up ? "text-[#be185d]" : "text-[#15803d]"}>
          {up ? "+" : "−"}{fmtUSD(Math.abs(k.value), { compact: true })}
          <span className="text-[10px] ml-1">({k.pct > 0 ? "+" : ""}{k.pct}%)</span>
        </span>
      );
    }
    if (k.fmt === "pct_delta") {
      const up = k.value >= 0;
      return (
        <span className={`inline-flex items-center gap-0.5 ${up ? "text-[#be185d]" : "text-[#15803d]"}`}>
          {up ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
          {Math.abs(k.value)}%
        </span>
      );
    }
    return `${k.value}%`;
  };
  return (
    <div className="cs-card px-4 py-3" data-testid={`exec-kpi-${k.id}`}>
      <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground truncate">{k.label}</div>
      <div className="cs-mono text-lg font-semibold mt-0.5">{val()}</div>
    </div>
  );
}

function Funnel({ stages }) {
  const max = stages[0]?.value || 1;
  return (
    <div className="space-y-2.5" data-testid="exec-savings-funnel">
      {stages.map((s, i) => (
        <div key={s.stage} className="flex items-center gap-3">
          <span className="text-[11px] text-muted-foreground w-24 shrink-0">{s.stage}</span>
          <div className="flex-1 h-7 bg-[hsl(var(--cs-tint))] rounded-sm overflow-hidden">
            <div className="h-full rounded-sm flex items-center px-2 transition-all duration-500"
              style={{ width: `${Math.max((s.value / max) * 100, 14)}%`, background: s.color }}>
              <span className="cs-mono text-[11px] text-white font-semibold whitespace-nowrap">{fmtUSD(s.value, { compact: true })}</span>
            </div>
          </div>
          {i > 0 && (
            <span className="cs-mono text-[10px] text-muted-foreground w-12 text-right">
              {Math.round((s.value / stages[i - 1].value) * 100)}%
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function Card({ title, children, testId, className = "" }) {
  return (
    <div className={`cs-card p-5 ${className}`} data-testid={testId}>
      <h3 className="cs-heading text-sm font-semibold tracking-tight mb-3">{title}</h3>
      {children}
    </div>
  );
}

function CfoTab({ data }) {
  const score = data.finops_score;
  const scoreColor = score >= 80 ? "#15803d" : score >= 60 ? "#f59e0b" : "#be185d";
  return (
    <div className="space-y-5">
      {/* AI Insight */}
      <div className="cs-card p-5 border-l-4" style={{ borderLeftColor: "#00579e" }} data-testid="exec-ai-insight">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-[#00579e] shrink-0 mt-0.5" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">CloudScore AI Insight · {data.period.label}</div>
            <p className="text-sm leading-relaxed text-foreground">{data.ai_insight}</p>
          </div>
          <div className="ml-auto shrink-0 text-center">
            <div className="w-16 h-16 rounded-full border-4 flex items-center justify-center" style={{ borderColor: scoreColor }}>
              <span className="cs-mono text-xl font-bold" style={{ color: scoreColor }}>{score}</span>
            </div>
            <div className="text-[9px] uppercase tracking-[0.1em] text-muted-foreground mt-1">FinOps Score</div>
          </div>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="exec-kpi-grid">
        {data.kpis.map((k) => <KpiCell key={k.id} k={k} />)}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card title="Actual vs Budget vs Forecast" testId="exec-avb" className="xl:col-span-2">
          <EconWidget def={{}} data={data.actual_vs_budget} size="md" />
        </Card>
        <Card title="Savings Funnel" testId="exec-funnel-card">
          <Funnel stages={data.savings_funnel} />
          <p className="text-[10px] text-muted-foreground mt-3">Identified → Approved → Implemented → Realized (monthly)</p>
        </Card>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card title="Spend by Business Unit" testId="exec-by-bu"><EconWidget def={{}} data={data.spend_by_business_unit} size="sm" /></Card>
        <Card title="Spend by Cloud" testId="exec-by-cloud"><EconWidget def={{}} data={data.spend_by_cloud} size="sm" /></Card>
        <Card title="Spend by Product" testId="exec-by-product"><EconWidget def={{}} data={data.spend_by_product} size="sm" /></Card>
        <Card title="Top Services" testId="exec-by-service"><EconWidget def={{}} data={data.spend_by_service} size="sm" /></Card>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card title="Top Cost Increases (MoM)" testId="exec-top-increases"><EconWidget def={{}} data={data.top_increases} /></Card>
        <Card title="Top Savings Opportunities" testId="exec-top-opps"><EconWidget def={{}} data={data.top_opportunities} /></Card>
      </div>
    </div>
  );
}

const EXEC_TABS = [
  { id: "cfo", l: "CFO Overview" },
  { id: "summary", l: "Executive Summary" },
];

export default function ExecOverview() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "cfo";
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    api.exec.overview().then(setOverview).catch(console.error);
  }, []);

  return (
    <div data-testid="exec-overview-page">
      <div className="px-6 lg:px-10 pt-8">
        <div className="flex items-center gap-1.5 border-b border-border flex-wrap">
          {EXEC_TABS.map((t) => (
            <button key={t.id} data-testid={`exec-tab-${t.id}`} onClick={() => setParams({ tab: t.id })}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors duration-150 ${
                tab === t.id ? "border-[#00579e] text-[#00579e]" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}>
              {t.l}
            </button>
          ))}
        </div>
      </div>
      {tab === "summary" ? (
        <Dashboard />
      ) : (
        <div className="px-6 lg:px-10 pt-6 pb-8">
          {tab === "cfo" && (overview ? <CfoTab data={overview} /> : <div className="cs-card p-10 text-center text-muted-foreground" data-testid="exec-loading">Loading CFO overview…</div>)}
        </div>
      )}
    </div>
  );
}
